import { Mail, Phone } from 'lucide-react';
import { WhatsAppButton } from './WhatsAppButton';
import { useLanguage } from '../context/LanguageContext';

export const FeaturedEventCard = ({ eventKey = 'wmOpening', testId }) => {
  const { t } = useLanguage();
  const events = t.featuredEvents || {};
  const ev = events[eventKey];
  if (!ev) return null;
  const accent = ev.accent || 'red'; // 'red' | 'pink' | 'blue'
  const accentMap = {
    pink: {
      badge: 'bg-pink-500/15 border border-pink-400/40 text-pink-200',
      dot: 'bg-pink-400',
      reserveBox: 'bg-pink-500/15 border border-pink-400/40',
      reserveLabel: 'text-pink-200',
    },
    blue: {
      badge: 'bg-sky-500/15 border border-sky-400/40 text-sky-200',
      dot: 'bg-sky-400',
      reserveBox: 'bg-sky-500/15 border border-sky-400/40',
      reserveLabel: 'text-sky-200',
    },
    red: {
      badge: 'bg-red-500/15 border border-red-500/40 text-red-300',
      dot: 'bg-red-500',
      reserveBox: 'bg-red-500/15 border border-red-500/40',
      reserveLabel: 'text-red-300',
    },
  };
  const a = accentMap[accent] || accentMap.red;
  const badgeClasses = a.badge;
  const dotClasses = a.dot;
  const reserveBoxClasses = a.reserveBox;
  const reserveLabelClasses = a.reserveLabel;

  return (
    <div
      className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-900 text-white shadow-xl"
      data-testid={testId || `featured-event-${eventKey}`}
    >
      <div className="grid md:grid-cols-2 gap-0">
        <a
          href={ev.image}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-black"
          data-testid={`featured-event-flyer-link-${eventKey}`}
        >
          <img
            src={ev.image}
            alt={ev.alt || ev.title}
            className="w-full h-full object-contain aspect-square md:aspect-auto"
            loading="lazy"
          />
        </a>
        <div className="p-6 md:p-10 flex flex-col justify-center">
          <div className={`inline-flex items-center gap-2 self-start px-3 py-1 rounded-full mb-4 ${badgeClasses}`}>
            <span className={`w-2 h-2 rounded-full ${dotClasses} animate-pulse`}></span>
            <span className="text-xs font-bold tracking-widest uppercase">{ev.badge}</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-2 leading-tight">
            {ev.title}
          </h3>
          <p className="text-lg md:text-xl text-slate-200 mb-6">{ev.matchup}</p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{ev.dateLabel}</p>
              <p className="text-lg font-bold">{ev.dateValue}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{ev.timeLabel}</p>
              <p className="text-lg font-bold">{ev.timeValue}</p>
            </div>
          </div>

          {ev.priceLabel && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{ev.priceLabel}</p>
              <p className="text-base font-bold text-white">{ev.priceValue}</p>
            </div>
          )}

          <div className={`rounded-2xl p-5 mb-6 ${reserveBoxClasses}`}>
            <p className={`text-sm font-bold uppercase tracking-widest mb-1 ${reserveLabelClasses}`}>{ev.reserveBadge}</p>
            <p className="text-base md:text-lg text-white leading-snug">{ev.reserveText}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {ev.emailTo ? (
              <a
                href={`mailto:${ev.emailTo}?subject=${encodeURIComponent(ev.emailSubject || ev.title || '')}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid={`featured-event-email-btn-${eventKey}`}
              >
                <Mail className="w-4 h-4" />
                {ev.emailCta || ev.emailTo}
              </a>
            ) : (
              <a
                href="tel:+41438330834"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid={`featured-event-call-btn-${eventKey}`}
              >
                <Phone className="w-4 h-4" />
                +41 43 833 0834
              </a>
            )}
            <WhatsAppButton
              text={ev.waText}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold border-2 border-white/30 text-white hover:bg-white/10 transition-colors"
              testId={`featured-event-wa-btn-${eventKey}`}
            >
              {ev.waCta}
            </WhatsAppButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Events Page
